import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { LabLyncPanel } from "@/components/LabLyncPanel";
import { Activity, Search, Lock, AlertTriangle, ArrowUpRight, Loader2 } from "lucide-react";

const num = (v) => (v === null || v === undefined ? "—" : v);

export default function LabLync() {
  const { can } = useAuth();
  const allowed = can("compliance:view");
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    if (!allowed) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/lablync/cases", { params: { q } });
      setRows(data.rows);
      setSource(data.source_system);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [q, allowed]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const critical = rows.filter((r) => r.critical_names.length).length;

  if (!allowed) {
    return (
      <div className="mx-auto max-w-xl border border-slate-200 px-6 py-14 text-center dark:border-slate-700"
           data-testid="lablync-no-access">
        <Lock size={22} className="mx-auto text-slate-400" />
        <h1 className="mt-3 font-display text-2xl font-bold text-[#134e4a] dark:text-teal-200">
          LabLync is for pathologist-level users
        </h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Clinical laboratory results are shown to pathologists, pathologists' assistants,
          supervisors and lab directors. Ask an administrator if you need access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lablync-page">
      <header className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#134e4a] dark:text-teal-200">
            LabLync — Clinical Lab Results
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
            Hematology results beside your bone marrow cases, so a marrow can be read without
            leaving PathQity. Display only — nothing here is edited or entered in PathQity.
          </p>
        </div>
        <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200"
              data-testid="lablync-page-readonly">
          <Lock size={11} /> Read-only
        </span>
      </header>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="border border-slate-200 p-4 dark:border-slate-700" data-testid="lablync-stat-cases">
          <p className="num font-display text-3xl font-bold text-[#134e4a] dark:text-teal-200">{rows.length}</p>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Bone marrow cases</p>
        </div>
        <div className="border border-slate-200 p-4 dark:border-slate-700" data-testid="lablync-stat-critical">
          <p className={`num font-display text-3xl font-bold ${critical ? "text-red-800" : "text-[#134e4a] dark:text-teal-200"}`}>{critical}</p>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">With a critical value</p>
        </div>
        <div className="border border-slate-200 p-4 dark:border-slate-700" data-testid="lablync-stat-source">
          <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
            <Activity size={15} className="text-[#134e4a]" /> {source || "—"}
          </p>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Result source</p>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="lablync-search"
               placeholder="Search accession, patient or MRN" className="pl-9" />
      </div>

      {loading ? (
        <Loader2 className="animate-spin text-slate-400" size={20} />
      ) : rows.length === 0 ? (
        <p className="border border-slate-200 px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700"
           data-testid="lablync-empty">
          No bone marrow cases found. LabLync covers bone marrow specimens only.
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <article key={r.case_id} className="border border-slate-200 dark:border-slate-700"
                     data-testid={`lablync-row-${r.case_id}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-3">
                <button onClick={() => setOpenId((c) => (c === r.case_id ? null : r.case_id))}
                        data-testid={`lablync-toggle-${r.case_id}`}
                        className="num text-left font-display text-lg font-bold text-[#134e4a] hover:underline dark:text-teal-200">
                  {r.accession_number}
                </button>
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  {r.patient_name} · {/^mrn/i.test(r.patient_mrn || "") ? "" : "MRN "}
                  {r.patient_mrn || "—"}
                </span>
                {r.critical_names.length > 0 ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-800 px-2 py-0.5 text-[11px] font-semibold text-white">
                    <AlertTriangle size={10} /> Critical
                  </span>
                ) : r.abnormal_count > 0 ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
                    {r.abnormal_count} abnormal
                  </span>
                ) : (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-900">
                    Within range
                  </span>
                )}
                <span className="num ml-auto text-xs text-slate-600 dark:text-slate-300">
                  WBC {num(r.wbc)} · Hgb {num(r.hemoglobin)} · Plt {num(r.platelets)} · Blasts {num(r.blasts)}%
                </span>
                <Link to={`/cases/${r.case_id}`} data-testid={`lablync-case-link-${r.case_id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#134e4a] hover:underline dark:text-teal-200">
                  Open the case <ArrowUpRight size={12} />
                </Link>
              </div>
              {openId === r.case_id && (
                <div className="border-t border-slate-200 p-3 dark:border-slate-700">
                  <LabLyncPanel caseId={r.case_id} />
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
